import { ComponentChild } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import ButtonToggle from '@bonniernews/dn-design-system-web/components/button-toggle/button-toggle.tsx'
import type { ButtonToggleProps } from '@bonniernews/dn-design-system-web/components/button/button-types.ts'

import { formatThousandsWithSpaces } from '../../helpers/formatThousandsWithSpaces.ts'

export interface ProfileHeaderProps extends SharedProps {
  /** H1 Title */
  title: string
  /** Subtitle (email) */
  subtitleHtml?: string
  /** Main image */
  media?: ComponentChild
  /** Intended use is text, links */
  descriptionHtml?: string
  /** Total articles on this list-page */
  articleCount?: number
  /** Show/hide toggle button */
  toggle?: boolean
  /** `text` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleText?: string
  /** `selectedText` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleSelectedText?: string
  /** `selected` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleSelected?: boolean
  /** `loading` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleLoading?: boolean
  /** `classNames` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleClassNames?: string
  /** `attributes` parameter to ToggleButton (only if `toggle` is enabled) */
  toggleAttributes?: ButtonToggleProps['attributes']
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/profile-header](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/profile-header)
 * - Storybook: [ProfileHeader](https://designsystem.dn.se/?path=/docs/section-profileheader--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/profile-header/profile-header.scss'`
 */
export const ProfileHeader = ({
  title,
  subtitleHtml,
  media,
  descriptionHtml,
  articleCount,
  toggle,
  toggleText,
  toggleSelectedText,
  toggleSelected = false,
  toggleLoading = false,
  toggleClassNames = '',
  toggleAttributes = {},
  classNames,
  attributes,
}: ProfileHeaderProps) => {
  const componentClassName = 'ds-profile-header'

  const classes = formatClassString([
    componentClassName,
    descriptionHtml || articleCount ? `${componentClassName}--has-border` : '',
    media ? `${componentClassName}--has-image` : '',
    classNames,
  ])

  return (
    <div className={classes} {...attributes}>
      <div className={`${componentClassName}__content`}>
        <div className={`${componentClassName}__bar`}>
          <div className={`${componentClassName}__heading`}>
            <h1 className={`${componentClassName}__title`}>{title}</h1>
            {subtitleHtml && (
              <p className={`${componentClassName}__subtitle`} dangerouslySetInnerHTML={{ __html: subtitleHtml }} />
            )}
            {toggle && (
              <ButtonToggle
                variant='primary'
                size='md'
                isIconButton={false}
                text={toggleText}
                selectedText={toggleSelectedText || ''}
                selected={toggleSelected}
                loading={toggleLoading}
                classNames={toggleClassNames}
                attributes={toggleAttributes}
              />
            )}
          </div>
          {media && <div className={`${componentClassName}__media`}>{media}</div>}
        </div>
        {descriptionHtml && (
          <div className={`${componentClassName}__description`} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        )}
        {articleCount !== undefined && articleCount > 0 && (
          <p className={`${componentClassName}__article-count`}>
            {formatThousandsWithSpaces(articleCount)} {articleCount === 1 ? 'artikel' : 'artiklar'}
          </p>
        )}
      </div>
    </div>
  )
}
